import React from "react";
import { useAnketaI18n } from "src/internationalization";

/*
 * Closing conversion band. Mirrors the navy hero canvas (aurora + faint grid)
 * to bookend the page, with a single strong "Book a demo" CTA - the deep CTA
 * placement competitor security sites use right before the footer.
 */

const tPrefix = "anketa.autonubil.home.final";

const ArrowRightIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AutonubilFinalSection = (): JSX.Element => {
  const { translate } = useAnketaI18n();

  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        isolation: "isolate",
        borderRadius: 24,
        padding: "clamp(48px, 7vw, 88px) clamp(24px, 6vw, 72px)",
        textAlign: "center",
        color: "var(--ak-on-navy)",
        background: "var(--ak-hero-bg)",
        border: "1px solid var(--ak-on-navy-border)",
        boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
      }}
    >
      {/* faint grid */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          backgroundImage:
            "linear-gradient(var(--ak-hero-grid) 1px, transparent 1px), linear-gradient(90deg, var(--ak-hero-grid) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(90% 120% at 50% 0%, #000 30%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(90% 120% at 50% 0%, #000 30%, transparent 75%)",
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        <div
          style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--ak-accent-2)",
            marginBottom: 14,
          }}
        >
          {translate(`${tPrefix}.eyebrow`, { defaultMessage: "Get started" } as any)}
        </div>

        <h2
          style={{
            margin: "0 auto",
            maxWidth: 720,
            fontSize: "clamp(1.9rem, 4vw, 3rem)",
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
            color: "var(--ak-on-navy)",
          }}
        >
          {translate(`${tPrefix}.title`, {
            defaultMessage: "Ready for enterprise security?",
          })}
        </h2>

        <p
          style={{
            margin: "18px auto 0",
            maxWidth: 600,
            fontSize: "clamp(1rem, 1.6vw, 1.18rem)",
            lineHeight: 1.65,
            color: "var(--ak-on-navy-secondary)",
          }}
        >
          {translate(`${tPrefix}.subtitle`, {
            defaultMessage:
              "Request a demo and see how opsanio protects your infrastructure.",
          })}
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 14,
            marginTop: 34,
          }}
        >
          <a
            href="/home/contact"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
              padding: "15px 30px",
              borderRadius: 10,
              fontSize: "1.02rem",
              fontWeight: 700,
              textDecoration: "none",
              color: "var(--ak-text-on-accent)",
              background:
                "linear-gradient(135deg, var(--ak-accent) 0%, var(--ak-accent-strong) 100%)",
              boxShadow: "0 8px 22px var(--ak-accent-glow)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 12px 30px var(--ak-accent-glow)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 22px var(--ak-accent-glow)";
            }}
          >
            {translate(`${tPrefix}.action.demo`, { defaultMessage: "Book a demo" })}
            <ArrowRightIcon />
          </a>

          <a
            href="/home/contact"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
              padding: "15px 28px",
              borderRadius: 10,
              fontSize: "1.02rem",
              fontWeight: 700,
              textDecoration: "none",
              color: "var(--ak-on-navy)",
              background: "var(--ak-on-navy-surface-2)",
              border: "1px solid var(--ak-on-navy-border)",
              transition: "background 0.2s ease, border-color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--ak-on-navy-surface)";
              e.currentTarget.style.borderColor = "var(--ak-accent-border)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--ak-on-navy-surface-2)";
              e.currentTarget.style.borderColor = "var(--ak-on-navy-border)";
            }}
          >
            {translate("anketa.contact", { defaultMessage: "Contact" })}
          </a>
        </div>
      </div>
    </section>
  );
};

export default AutonubilFinalSection;
